import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import sanitize from 'sanitize-html';

import { Link } from '@/design-system/atoms/Link';
import { useObserver } from '@/hooks/useObserver';

import styles from './PatientGptCta.module.scss';

// Sanitize label HTML to <strong> only — no attributes, no other tags.
const LABEL_HTML_OPTIONS = { allowedTags: ['strong'], allowedAttributes: {} };
// Strip all tags to produce a plain-text string for aria-label.
const STRIP_TAGS_OPTIONS = { allowedTags: [], allowedAttributes: {} };

// sanitize-html re-encodes text content (& → &amp; etc.). Attributes set via
// the JS DOM API are not HTML-decoded by browsers, so we decode the 5 entities
// sanitize-html produces before using the string as an aria-label value.
const HTML_ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
const decodeHtmlEntities = (str) =>
    str.replace(/&(?:amp|lt|gt|quot|#39);/g, (m) => HTML_ENTITIES[m]);

// IntersectionObserver threshold — animate-on-view tracks live viewport visibility;
// the animation restarts each time the wrapper crosses ~30% (AHSMKTG-4129).
const IN_VIEW_THRESHOLD = 0.3;

/**
 * PatientGptCta — presentational design-system molecule for the PatientGPT
 * secondary CTA. Wraps the `Link` atom in a non-interactive animated
 * gradient-border shell (static on load, animates on each viewport entry and on
 * hover), with a `prefers-reduced-motion` fallback that keeps the border
 * static. The IntersectionObserver is always attached; animation is suppressed
 * via `prefersReducedMotion`, not by skipping the hook.
 */
const PatientGptCta = ({ href, label, ariaLabel = undefined, role = undefined }) => {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    });
    const { ref: wrapperRef, inView: isInView } = useObserver({ threshold: IN_VIEW_THRESHOLD });

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const isAnimated = !prefersReducedMotion && isInView;
    const { htmlLabel, plainLabel } = useMemo(
        () => ({
            htmlLabel: sanitize(label, LABEL_HTML_OPTIONS),
            plainLabel: decodeHtmlEntities(sanitize(label, STRIP_TAGS_OPTIONS)),
        }),
        [label],
    );
    const accessibleName = `${ariaLabel || plainLabel} (opens in a new tab)`;

    return (
        <div
            ref={wrapperRef}
            data-testid="patient-gpt-cta"
            className={classNames(styles.wrapper, isAnimated && styles['wrapper--animated'])}
        >
            <Link
                className={styles.link}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={accessibleName}
                role={role}
            >
                <span dangerouslySetInnerHTML={{ __html: htmlLabel }} />
            </Link>
        </div>
    );
};

PatientGptCta.propTypes = {
    /**
     * Destination URL — resolved upstream by the caller (a client-side session
     * URL from the PatientGPT Session Provider) and consumed as-is, never mutated.
     */
    href: PropTypes.string.isRequired,
    /**
     * Visible CTA text rendered as HTML. Accepts a limited subset — `<strong>` only,
     * no attributes — sanitized via sanitize-html before render. Plain-text values work
     * unchanged. A stripTags() pass derives the accessible-name fallback when `ariaLabel`
     * is absent.
     */
    label: PropTypes.string.isRequired,
    /**
     * Accessible-name override. Falls back to `label` when omitted.
     */
    ariaLabel: PropTypes.string,
    /**
     * ARIA role forwarded verbatim to the composed `Link` (the sole focusable
     * element). Lets a caller place this CTA inside a `role="menu"` ancestor
     * (`role="menuitem"`) without putting the role on the non-focusable
     * gradient-border wrapper.
     */
    role: PropTypes.string,
};

export default PatientGptCta;
